import React, { useState, useRef, useEffect } from "react";
import "./SideFeature.css";
import { ArrowUp, MessageCircle, Send } from "lucide-react";
// Bot avatar removed per design — no external avatar used

const initialQuestions = [
  { q: "How do I track my order?", a: "To view your order status, click the profile icon at the top right corner and select \"My Orders\"." },
  { q: "What payment methods are available?", a: "We currently accept Online Banking and FPX payments." },
  { q: "How do I return or exchange an item?", a: "For return or exchange enquiries, please contact our customer support team via WhatsApp at +6017-3750336." },
  { q: "How long does delivery take?", a: "Delivery within Klang Valley typically takes 1–3 business days after order confirmation. For East Malaysia and other regions, please contact us after your order is confirmed to obtain the estimated delivery time." },
  { q: "I want to contact customer service", a: "Please describe your issue below and our support team will assist you." },
];

const SideFeaturev2 = () => {
  const [showChat, setShowChat] = useState(false);
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hello! 👋 I’m your shopping assistant." },
    { from: "bot", text: "How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [showInput, setShowInput] = useState(true); // allow typing by default
  const inputRef = useRef(null);
  const [availableQuestions, setAvailableQuestions] = useState(initialQuestions);
  const [showTopBtn, setShowTopBtn] = useState(false);
  const modalRef = useRef(null);
  const chatBodyRef = useRef(null);
  const [botTyping, setBotTyping] = useState(false);
  // Close chat when clicking outside
  useEffect(() => {
    if (!showChat) return;
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowChat(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showChat]);

  // show Top button only when scrolled away from top
  useEffect(() => {
    const onScroll = () => {
      setShowTopBtn(window.pageYOffset > 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Auto scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, showChat]);

  // focus input when chat opens
  useEffect(() => {
    if (showChat) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 160);
    }
  }, [showChat]);

  const handleGoToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOpenChat = () => {
    setShowChat(true);
    setClosing(false);
  };

  const handleCloseChat = () => {
    setClosing(true);
    setTimeout(() => {
      setShowChat(false);
      setClosing(false);
    }, 250); // match animation duration
  };

  const handleGeneralQuestion = (item) => {
    setMessages((msgs) => [
      ...msgs,
      { from: "user", text: item.q, isOption: true }
    ]);
    setBotTyping(true);

    if (item.q === "I want to contact customer service") {
      window.open(
        "https://api.whatsapp.com/send/?phone=60173750336&text&type=phone_number&app_absent=0",
        "_blank"
      );
      setShowInput(false);
      setTimeout(() => {
        setMessages((msgs) => [
          ...msgs,
          { from: "bot", text: item.a }
        ]);
        setBotTyping(false);
      }, 700);
      return;
    }

    setTimeout(() => {
      setMessages((msgs) => [
        ...msgs,
        { from: "bot", text: item.a }
      ]);
      setBotTyping(false);
      if (item.q !== "I want to contact customer service") setShowInput(true);
    }, 900);
  };


 const handleSend = () => {
    if (!input.trim()) return;
    setMessages((msgs) => [
      ...msgs,
      { from: "user", text: input }
    ]);
    setInput("");
    setBotTyping(true);
    setTimeout(() => {
      setMessages((msgs) => [
        ...msgs,
        { from: "bot", text: "Thank you for contacting us. Our customer support team will respond to your enquiry as soon as possible. For urgent assistance, please reach us via WhatsApp at +6017-3750336." }
      ]);
      setBotTyping(false);
    }, 900);
  };

  // (attachment upload removed) 

  return (
    <div className="sidefeature_v2">
      <div className="sidefeature-content_v2">
        {showTopBtn && (
          <div className="gototop_v2">
            <button
              className="sidefeature-button_v2 gototop-button_v2"
              onClick={handleGoToTop}
              aria-label="Go to top"
            >
              <ArrowUp className="side-icon_v2" size={20} />
            </button>
          </div>
        )}
        <div className="chatbot_v2">
          <button
            className="sidefeature-button_v2 chatbot-button_v2"
            onClick={() => setShowChat(s => !s)}
            aria-label="Toggle chat"
          >
            <MessageCircle className="side-icon_v2" size={20} />
          </button>
        </div>
      </div>
      {showChat && (
        <div className="chat-modal-overlay_v2">
          <div className="chat-modal_v2 chat-modal-custom-large_v2" ref={modalRef}>
            <div className="chat-header_v2 chat-header-custom_v2">
              <div className="chat-brand_v2">
                <div className="chat-brand-icon_v2">
                  <MessageCircle size={18} />
                </div>
                <span className="chat-brand-title_v2">Chat Support</span>
              </div>
              <button className="chat-close_v2" onClick={handleCloseChat}>×</button>
            </div>
             <div className="chat-questions-fixed_v2">
              {initialQuestions.map((item, idx) => (
                <button
                  className="chat-question-btn_v2 chat-question-btn-fixed_v2"
                  key={idx}
                  onClick={() => handleGeneralQuestion(item)}
                >
                  {item.q}
                </button>
              ))}
            </div>
            <div className="chat-body_v2" ref={chatBodyRef}>
              {messages.map((msg, idx) =>
                msg.from === "bot" ? (
                  <div key={idx} className="chat-msg-row_v2 bot_v2">
                    <div className="chat-msg_v2 bot_v2">
                      <div className="chat-msg-text_v2">{msg.text}</div>
                      <div className="chat-msg-meta-inside_v2">Support Team</div>
                    </div>
                  </div>
                ) : (
                  <div key={idx} className="chat-msg-row_v2 user_v2">
                    <div className="chat-msg_v2 user_v2">{msg.text}</div>
                  </div>
                )
              )}
               {botTyping && (
                  <div className="chat-msg-row_v2 bot_v2">
                    <div className="chat-msg_V2 bot_v2">
                      <span className="typing-dots_v2">
                        <span className="typing-dot_v2"></span>
                        <span className="typing-dot_v2"></span>
                        <span className="typing-dot_v2"></span>
                      </span>
                      <div className="chat-msg-meta-inside_v2">Support Team</div>
                    </div>
                  </div>
                )}
            </div>
            <div className="chat-input-row_v2 chat-input-row-custom_v2">
              <input
                ref={inputRef}
                className="chat-input_v2"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={
                  showInput
                    ? "Type your message here..."
                    : "Please select an option above"
                }
                disabled={!showInput}
                onKeyDown={e => showInput && e.key === "Enter" && handleSend()}
              />
              {/* attachment removed */}
              <button
                className="chat-send-btn_v2"
                onClick={handleSend}
                disabled={!showInput}
                style={{ opacity: showInput ? 1 : 0.5 }}
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideFeaturev2;